import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";

type User = { id: string; email: string; email_verified: boolean; is_platform_admin: boolean; org: { id: string; name: string; slug: string } | null };
type Ctx = { user: User | null | false; setUser: (u: User | false) => void; reload: () => Promise<void>; logout: () => Promise<void> };

const AuthContext = createContext<Ctx>({} as Ctx);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null | false>(null);
  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const logout = async () => {
    await api.post("/auth/logout").catch(() => null);
    setUser(false);
  };
  return <AuthContext.Provider value={{ user, setUser, reload, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

export function Protected({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="auth-loading">Loading…</div>;
  if (user === false) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  return <>{children}</>;
}
